import utils from "@/utils";

/**
 * Legacy (2025) WPD table saves carry a single `calibration.cellTagGroup` and
 * no per-entry `tagGroup` on the numeric-key metadata entries of the row
 * headers, column headers and cell pixels. The extraction UI repairs that in
 * memory at load via `wpd.alignAxes.backfillTableTagGroups` in
 * `web/src/plugins/wpd/table-axis/functions.js`, but that function works on
 * wpd axis/dataset objects (`getMetadata`, `getAllPixels`, ...). The Raw Data
 * Viewer reads the plain JSON from `GET /data-entry/:uuid/raw-data-viewer`
 * and never runs it, so `tableCellHasNonHeaderData` in
 * `VRawDataViewerData.vue` filters every entry out and the whole table is
 * silently absent (Nowak 2020: 10 tables, 0 of ~5,000 entries tagged). This
 * is the same backfill, applied to the raw JSON.
 *
 * The rules mirror the plugin function exactly:
 * - only an axis with `cellTagGroup` set and no `cellTagGroups` is touched
 * - `cellTagGroups` becomes `[cellTagGroup]`
 * - every numeric-key entry on `calibration.rows.headers[*]`,
 *   `calibration.cols.headers[*]` and the metadata of every pixel of every
 *   dataset on the axis gets `tagGroup = cellTagGroup` when it has none
 *
 * Returns `{ axesColl, datasetColl }`. The inputs are never mutated (they are
 * the component's `rawData` prop): the axes that need the backfill and the
 * datasets on them are cloned, everything else is returned by reference. A
 * document that already carries per-entry tag groups comes back untouched.
 */

function tableAxisNeedsBackfill(axis) {
	const calibration = axis?.metadata?.table ? axis.metadata.calibration : null;
	return !!(calibration?.cellTagGroup && !calibration.cellTagGroups);
}

function backfillEntries(container, cellTagGroup) {
	if (!container) return;
	Object.keys(container)
		.filter((key) => utils.isNumber(key))
		.forEach((key) => {
			const value = container[key];
			if (value && !value.tagGroup) {
				value.tagGroup = cellTagGroup;
			}
		});
}

function clone(value) {
	return JSON.parse(JSON.stringify(value));
}

export function backfillTableTagGroups(axesColl, datasetColl) {
	if (!Array.isArray(axesColl) || !axesColl.some(tableAxisNeedsBackfill)) {
		return { axesColl, datasetColl };
	}

	const cellTagGroupByAxisName = new Map();

	const backfilledAxes = axesColl.map((axis) => {
		if (!tableAxisNeedsBackfill(axis)) return axis;

		const copy = clone(axis);
		const calibration = copy.metadata.calibration;
		const cellTagGroup = calibration.cellTagGroup;
		calibration.cellTagGroups = [cellTagGroup];

		const rowHeaders = calibration.rows?.headers ?? [];
		const colHeaders = calibration.cols?.headers ?? [];
		[...rowHeaders, ...colHeaders].forEach((header) =>
			backfillEntries(header, cellTagGroup),
		);

		cellTagGroupByAxisName.set(copy.name, cellTagGroup);
		return copy;
	});

	const backfilledDatasets = Array.isArray(datasetColl)
		? datasetColl.map((dataset) => {
				if (!cellTagGroupByAxisName.has(dataset.axesName)) return dataset;

				const cellTagGroup = cellTagGroupByAxisName.get(dataset.axesName);
				const copy = clone(dataset);
				(copy.data ?? []).forEach((pixel) =>
					backfillEntries(pixel.metadata, cellTagGroup),
				);
				return copy;
			})
		: datasetColl;

	return { axesColl: backfilledAxes, datasetColl: backfilledDatasets };
}
